import { Check, Quote } from "lucide-react";
import { BRAND_STORY as S, IMG } from "@/data/site";

export const BrandStory = () => (
  <section id="why-zoryven" className="relative border-y border-emerald-900/40 bg-[#06140E] overflow-hidden" data-testid="why-zoryven-story-section">
    <div className="absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-emerald-500/5 to-transparent pointer-events-none" />

    {/* Opening — editorial */}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-4">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#E5A862]">{S.eyebrow}</span>
          <h2 className="font-condensed text-5xl sm:text-6xl lg:text-7xl uppercase text-white mt-4 leading-[0.95]" data-testid="brand-story-headline">
            {S.headline}
          </h2>
          <div className="mt-8 h-px w-24 bg-[#D48C46]" />
        </div>
        <div className="lg:col-span-7 lg:col-start-6 space-y-6">
          <p className="text-2xl sm:text-3xl text-emerald-50 font-light leading-snug first-letter:text-6xl first-letter:font-bold first-letter:text-[#D48C46] first-letter:float-left first-letter:mr-3 first-letter:leading-[0.8]" data-testid="why-zoryven-intro">
            {S.opening[0]}
          </p>
          {S.opening.slice(1).map((p) => (
            <p key={p} className="text-base sm:text-lg text-slate-300 leading-relaxed">{p}</p>
          ))}
        </div>
      </div>
    </div>

    {/* Journey — numbered goals */}
    <div className="border-t border-emerald-900/40 bg-[#0B1E16]/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="font-condensed text-[7rem] sm:text-[9rem] leading-none text-[#D48C46]/20 -mb-10">10</div>
          <h3 className="font-condensed text-3xl sm:text-4xl uppercase text-white relative">{S.journey.title}</h3>
          <p className="mt-5 text-base text-slate-300 leading-relaxed">{S.journey.body}</p>
        </div>
        <div className="lg:col-span-7">
          <p className="mb-4 text-sm font-semibold text-emerald-200">{S.journey.lead}</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="brand-story-goals">
          {S.journey.goals.map((g, i) => (
            <li key={g} className="flex items-start gap-4 p-5 rounded-2xl bg-[#06140E] border border-emerald-900/60 hover:border-[#D48C46]/50 transition-colors">
              <span className="font-condensed text-2xl text-[#D48C46] leading-none">0{i + 1}</span>
              <span className="text-sm text-slate-200 leading-relaxed">{g}</span>
            </li>
          ))}
          </ul>
        </div>
      </div>
    </div>

    {/* Care + Promise */}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="relative rounded-[2rem] overflow-hidden border border-emerald-900/60 min-h-[22rem]">
        <img src={IMG.hairOil} alt="SMBHAV Pure Ayurveda Hair Oil" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#06140E] via-[#06140E]/70 to-[#06140E]/20" />
        <div className="relative p-8 sm:p-10 flex flex-col justify-end h-full min-h-[22rem]">
          <h3 className="font-condensed text-3xl sm:text-4xl uppercase text-white">{S.care.title}</h3>
          {S.care.body.map((p) => (
            <p key={p} className="mt-3 text-sm sm:text-base text-slate-200 leading-relaxed">{p}</p>
          ))}
        </div>
      </div>

      <div className="rounded-[2rem] border border-[#D48C46]/30 bg-gradient-to-br from-[#1E120B] to-[#06140E] p-8 sm:p-10 flex flex-col justify-between" data-testid="brand-story-promise">
        <div>
          <Quote className="w-8 h-8 text-[#D48C46]" />
          <span className="block mt-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#E5A862]">{S.promise.title}</span>
          <p className="font-condensed text-3xl sm:text-5xl uppercase text-white mt-3 leading-tight">{S.promise.statement}</p>
          <p className="mt-6 text-sm sm:text-base text-slate-300 leading-relaxed">{S.promise.body}</p>
        </div>
        <div className="mt-8 pt-6 border-t border-[#D48C46]/20 space-y-1">
          {S.promise.closing.map((c) => (
            <p key={c} className="text-base sm:text-lg text-emerald-50 italic flex items-start gap-2"><Check className="w-4 h-4 mt-1.5 text-[#D48C46] shrink-0" />{c}</p>
          ))}
        </div>
      </div>
    </div>

    {/* Welcome banner */}
    <div className="border-t border-emerald-900/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-testid="brand-story-welcome">
        <h3 className="font-condensed text-4xl sm:text-6xl uppercase text-white">{S.welcome.title}</h3>
        <p className="mt-3 text-base sm:text-xl text-[#E5A862] font-medium">{S.welcome.sub}</p>
        <p className="mt-6 text-xs sm:text-sm uppercase tracking-[0.3em] text-slate-400">{S.welcome.sign}</p>
      </div>
    </div>
  </section>
);
